import {
  Notification,
  NotificationsFilters,
  NotificationsPagination,
} from '@/types/notification.types';
import { create } from 'zustand';

interface NotificationsState {
  // Data
  notifications: Notification[];
  totalCount: number;
  unreadCount: number;
  totalPages: number;

  // Pagination
  currentPage: number;
  limit: number;

  // Filters
  searchQuery: string;
  statusFilter: 'all' | 'success' | 'failed';
  typeFilter: 'all' | 'database' | 'email';
  readFilter: 'all' | 'read' | 'unread';

  // Loading states
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;

  // UI state
  isPopoverOpen: boolean;
}

interface NotificationsActions {
  // Data actions
  setNotifications: (notifications: Notification[]) => void;
  addNotifications: (notifications: Notification[]) => void;
  updateNotification: (notification: Notification) => void;
  removeNotification: (notificationId: string) => void;
  setUnreadCount: (count: number) => void;
  setPagination: (totalCount: number, totalPages: number) => void;

  // Filter actions
  setSearchQuery: (query: string) => void;
  setStatusFilter: (status: 'all' | 'success' | 'failed') => void;
  setTypeFilter: (type: 'all' | 'database' | 'email') => void;
  setReadFilter: (filter: 'all' | 'read' | 'unread') => void;

  // Pagination actions
  setCurrentPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  goToPage: (page: number) => void;

  // Loading actions
  setLoading: (loading: boolean) => void;
  setLoadingMore: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // UI actions
  setPopoverOpen: (open: boolean) => void;
  togglePopover: () => void;

  // Computed getters
  getFilters: () => NotificationsFilters;
  getPagination: () => NotificationsPagination;

  // Reset actions
  reset: () => void;
}

const initialState: NotificationsState = {
  notifications: [],
  totalCount: 0,
  unreadCount: 0,
  totalPages: 0,
  currentPage: 1,
  limit: 10,
  searchQuery: '',
  statusFilter: 'failed',
  typeFilter: 'all',
  readFilter: 'all',
  isLoading: false,
  isLoadingMore: false,
  error: null,
  isPopoverOpen: false,
};

export const useNotificationsStore = create<
  NotificationsState & NotificationsActions
>()((set, get) => ({
  ...initialState,

  // Data actions
  setNotifications: notifications => set({ notifications }),
  addNotifications: newNotifications =>
    set(state => ({
      notifications: [...state.notifications, ...newNotifications],
    })),
  updateNotification: updatedNotification =>
    set(state => ({
      notifications: state.notifications.map(notification =>
        notification.uuid === updatedNotification.uuid
          ? updatedNotification
          : notification
      ),
    })),
  removeNotification: notificationId =>
    set(state => ({
      notifications: state.notifications.filter(
        notification => notification.uuid !== notificationId
      ),
      totalCount: Math.max(0, state.totalCount - 1),
    })),
  setUnreadCount: unreadCount => set({ unreadCount }),
  setPagination: (totalCount, totalPages) => set({ totalCount, totalPages }),

  // Filter actions
  setSearchQuery: searchQuery => set({ searchQuery, currentPage: 1 }),
  setStatusFilter: statusFilter => set({ statusFilter, currentPage: 1 }),
  setTypeFilter: typeFilter => set({ typeFilter, currentPage: 1 }),
  setReadFilter: readFilter => set({ readFilter, currentPage: 1 }),

  // Pagination actions
  setCurrentPage: currentPage => set({ currentPage }),
  nextPage: () =>
    set(state => ({
      currentPage: Math.min(state.totalPages, state.currentPage + 1),
    })),
  prevPage: () =>
    set(state => ({
      currentPage: Math.max(1, state.currentPage - 1),
    })),
  goToPage: page =>
    set(state => ({
      currentPage: Math.max(1, Math.min(state.totalPages, page)),
    })),

  // Loading actions
  setLoading: isLoading => set({ isLoading }),
  setLoadingMore: isLoadingMore => set({ isLoadingMore }),
  setError: error => set({ error }),

  // UI actions
  setPopoverOpen: isPopoverOpen => set({ isPopoverOpen }),
  togglePopover: () => set(state => ({ isPopoverOpen: !state.isPopoverOpen })),

  // Computed getters
  getFilters: () => {
    const state = get();
    return {
      search: state.searchQuery || undefined,
      status: state.statusFilter !== 'all' ? state.statusFilter : undefined,
      type: state.typeFilter !== 'all' ? state.typeFilter : undefined,
      isRead:
        state.readFilter === 'read'
          ? true
          : state.readFilter === 'unread'
            ? false
            : undefined,
    };
  },

  getPagination: () => {
    const state = get();
    return {
      page: state.currentPage,
      limit: state.limit,
      skip: (state.currentPage - 1) * state.limit,
    };
  },

  // Reset actions
  reset: () => set(initialState),
}));
